










































































































































/* General */
import Vue from 'vue'
import Quill from 'quill'
import DragAndDropModule from 'quill-drag-and-drop-module'
import quillMention from 'quill-mention'
import * as linkify from 'linkifyjs'
import Clickoutside from 'element-ui/src/utils/clickoutside'

/* Mixins */

/* Components */
import GooglePicker from '@/components/editor/components/editor-google-picker.vue'
import EditorSavedReplies from '@/components/editor/components/editor-saved-replies.vue'
import EditorSavedTemplates from '@/components/editor/components/editor-saved-templates.vue'

/* Helpers */
import ClipboardJS from 'clipboard'

/* Data types */
import { IBrowser } from './editor.types'
import { IAttachment } from '@/types/data-objects/attachments'
import {
  IAtMentionUser,
  IConvertedSavedReply,
  IDescriptionTemplate,
  INameAndDescription,
} from '@/api/types/response.types'

/* API */
import FilesAPI from '@/api/modules/files.api'
import TicketPortalAPI from '@/api/modules/ticket-portal.api'

Quill.register('modules/mention', quillMention)

/**
 * WYSIWYG editor using QuillJS
 */
export default Vue.extend({
  name: 'Editor',

  directives: { Clickoutside },

  components: {
    GooglePicker,
    EditorSavedReplies,
    EditorSavedTemplates,
  },
  props: {
    value: {
      type: String,
      default: '',
    },

    name: {
      type: String,
      default: 'editor',
    },

    fixedHeight: {
      type: Boolean,
      default: false,
    },

    /**
     * Reply signature
     */
    signature: {
      type: String,
      default: '',
    },

    /**
     * Array of elements to be mentioned by at
     */
    mentionUsers: {
      type: Array as () => IAtMentionUser[],
      default: (): IAtMentionUser[] => [],
    },

    /**
     * Array of strings with custom variables
     */
    variables: {
      type: Array as () => INameAndDescription[],
      required: false,
      default: (): INameAndDescription[] => [],
    },

    placeholder: {
      type: String,
      default: '',
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    allowsAttachment: {
      type: Boolean,
      default: false,
    },

    allowsGoogleDrive: {
      type: Boolean,
      default: false,
    },

    // When coupled with allowsAttachment, will send file upload requests
    // to ticket portal customer API instead of normal account membership files API...
    ticketPortalConvoId: {
      type: Number,
      default: null,
    },

    // Pre-stub attachments list if necessary (e.g. this is used for editing an existing comment)
    existingAttachments: {
      type: Array as () => IAttachment[],
      default: (): IAttachment[] => [],
    },

    autofocus: {
      type: Boolean,
      default: false,
    },

    savedReplies: {
      type: Array as () => IConvertedSavedReply[],
      default: (): IConvertedSavedReply[] => [],
    },

    savedTemplates: {
      type: Array as () => IDescriptionTemplate[],
      default: (): IDescriptionTemplate[] => [],
    },

    clearAttachments: {
      type: Boolean,
      default: false,
    },

    flatBottom: {
      type: Boolean,
      default: false,
    },

    disableToolbarStickyness: {
      type: Boolean,
      default: false,
    },

    /* Overrides any top pixel value for the sticky toolbar with the inputed value.*/
    customStickyTopPixels: {
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      editor: null,
      clipboard: null,
      copyableVariableClass: 'copyable-var',
      showVariableDropdown: false,
      output: '',
      matches: [] as any[],
      fileList: [],
      filelistRemoved: [],
      isEditorFocused: false,
      hasAttachment: false,
      uploadingFile: false,
      browser: this.$deviceInfo.getBrowser as IBrowser,
      isMobileActionSheetOpen: false,
      dragEnter: false,
      dragOver: false,
      variableValueAndLabelDelimiter: '###',
    }
  },

  computed: {
    hasVariables(): boolean {
      return this.variables.length > 0
    },
    hasLinkInVariables(): boolean {
      if (!this.hasVariables) {
        return false
      }
      return this.variables.some(v => this.isVariableAUrl(v))
    },
    hasSavedReplies(): boolean {
      return this.savedReplies.length > 0
    },
    hasSavedTemplates(): boolean {
      return this.savedTemplates.length > 0
    },
    quillMentionUsers(): any {
      /* Quill requires id, value fields so remap the mentionUsers here */
      return this.mentionUsers.map((item: any) => ({
        id: item.id,
        value: item.label,
        label: item.name,
      }))
    },
    isInternetExplorer(): boolean {
      return this.browser.name === 'Internet Explorer'
    },
    isFirefox(): boolean {
      return this.browser.name === 'Firefox'
    },
    isSafari(): boolean {
      return this.browser.name === 'Safari'
    },
  },

  watch: {
    value(value, oldValue) {
      if (value !== this.output && !this.isInternetExplorer) {
        const vm = this
        this.editor.setContents(vm.editor.clipboard.convert(value + '\n'))
      }
      if (value === '') {
        this.fileList = []
        this.hasAttachment = false
      }
    },
    existingAttachments(value) {
      if (value && value.length === 0) {
        this.hasAttachment = false
      }
      this.fileList = value
    },

    /**
     * Clear file list when true
     */
    clearAttachments(value: boolean) {
      if (value) {
        this.fileList = []
        this.hasAttachment = false
        this.$emit('cleared-attachments')
      }
    },

    autofocus: {
      immediate: true,
      async handler(value) {
        this.isEditorFocused = value
        if (value) {
          await this.$nextTick()
          // @ts-ignore
          this.editor.focus()
        }
      },
    },
  },

  mounted() {
    if (this.hasLinkInVariables) {
      this.clipboard = new ClipboardJS('.' + this.copyableVariableClass)
      this.clipboard.on('success', (e: any) => {
        // @ts-ignore
        this.$messaging.showSuccess('Copied to your clipboard!')
        e.clearSelection()
      })
    }
    const IMAGE_MIME_REGEX = /^image\/(p?jpg|jpeg|gif|png)$/i

    if (this.existingAttachments.length) {
      this.fileList = this.existingAttachments
    }

    this.hasAttachment = this.fileList.length > 0

    /**
     * Shared quill modules
     */
    const modules = {
      toolbar: `#toolbar-${this.name}`,
      /**
       * Module Mention
       */
      mention: {
        allowedChars: /^[A-Za-z\sÅÄÖåäö]*$/,
        mentionDenotationChars: ['@'],
        defaultMenuOrientation: 'bottom',
        renderItem: (item: any, searchTerm: String) => {
          return `<span class="mention--dark">${
            item.label
          }</span> <span class="mention--grey">@${item.value}</span>`
        },
        source: (searchTerm: String, renderList: any, mentionChar: any) =>
          this.searchMentions(searchTerm, renderList, mentionChar),
      },
    }

    /**
     * Custom quill icons
     */
    const icons = Quill.import('ui/icons')
    icons.bold = '<i class="ddi ddi-bold toolbar-icon"/>'
    icons.header['2'] = '<i class="ddi ddi-heading toolbar-icon"/>'
    icons.italic = '<i class="ddi ddi-italic toolbar-icon"/>'
    icons.image = '<i class="ddi ddi-image toolbar-icon"/>'
    icons.underline = '<i class="ddi ddi-underline toolbar-icon"/>'
    icons.link = '<i class="ddi ddi-link toolbar-icon"/>'
    icons.blockquote = '<i class="ddi ddi-quotes toolbar-icon"/>'
    icons['code-block'] = '<i class="ddi ddi-code toolbar-icon"/>'
    icons.list.bullet = '<i class="ddi ddi-bulleted-list toolbar-icon"/>'
    icons.list.ordered = '<i class="ddi ddi-numbered-list toolbar-icon"/>'

    /**
     * Shared quill configurations
     */
    const configQuill = {
      placeholder: this.placeholder,
      theme: 'snow',
      bounds: this.$refs.editorCanvas, // Ensures tooltip stays inside the ql-editor boundary (and doesn't veer under toolbar, for instance)
      formats: [
        'bold',
        'italic',
        'link',
        'strike',
        'underline',
        'script',
        'blockquote',
        'header',
        'indent',
        'list',
        'code-block',
        'direction',
        'image',
        'video',
        'mention',
      ], // Whitelist formatting options (removes inline styling from being applied on copy-paste)
    }

    // TODO find a correction to quill drag and drop (WeakMap) not supported in IE11
    if (this.isInternetExplorer) {
      this.editor = new Quill(this.$refs.editorCanvas, {
        modules,
        ...configQuill,
      })
    } else {
      this.editor = new Quill(this.$refs.editorCanvas, {
        modules: {
          ...modules,
          dragAndDrop: {
            draggables: [
              {
                content_type_pattern:
                  DragAndDropModule.image_content_type_pattern,
                tag: 'img',
                attr: 'src',
              },
              {
                content_type_pattern: '^(.(?!image))*$',
                tag: 'img',
                attr: 'src',
              },
            ],
            onDrop: (file: any) => {
              if (!this.allowsAttachment) {
                return false
              } else if (IMAGE_MIME_REGEX.test(file.type)) {
                this.dragEnter = false
                this.dragOver = false
                this.$emit('open-editor')
                return DragAndDropModule.utils.getFileDataUrl(file)
              } else {
                this.dragEnter = false
                this.dragOver = false
                this.$emit('open-editor')
                const fileToUpload = {
                  ...file,
                  status: 'ready',
                  name: file.name,
                  size: file.size,
                  percentage: 0,
                  raw: file,
                  uid: Date.now() + file.id,
                }
                this.handleChange(fileToUpload, this.fileList)
                return false
              }
            },
          },
        },
        ...configQuill,
      })

      this.$refs.editorCanvas
        // @ts-ignore
        .querySelector('.ql-editor')
        .classList.add('user-inputed-rich-text')

      if (this.customStickyTopPixels !== null) {
        this.$refs.editor
          // @ts-ignore
          .querySelector('.ql-toolbar').style.cssText = `top: ${
          this.customStickyTopPixels
        }px;`
      }
    }

    // Change the link placeholder
    const tooltip = this.editor.theme.tooltip
    const input = tooltip.root.querySelector('input[data-link]')
    input.dataset.link = 'Enter link address here...'

    this.editor.root.innerHTML = this.value

    this.editor.enable(!this.disabled)

    this.editor.setHTML = (html: any) => {
      this.editor.root.innerHTML = html
    }

    // @ts-ignore
    const pasteImage = file => {
      const reader = new FileReader()
      reader.onload = e => {
        const img = document.createElement('img')
        // @ts-ignore
        img.src = e.target.result
        const range = window.getSelection().getRangeAt(0)
        range.deleteContents()
        range.insertNode(img)
      }
      reader.readAsDataURL(file)
    }

    /* Copy/paste images work out-of-the-box in Chrome/FF
       This is to handle Safari. IE/Edge plain doesn't work so skip. */
    document.querySelector('.editor').addEventListener('paste', e => {
      if (
        this.isSafari &&
        e &&
        // @ts-ignore
        e.clipboardData &&
        // @ts-ignore
        e.clipboardData.items
      ) {
        // @ts-ignore
        const items = e.clipboardData.items
        for (const item of items) {
          if (IMAGE_MIME_REGEX.test(item.type)) {
            pasteImage(item.getAsFile())
            return
          }
        }
      }
    })

    /**
     * Autolink pasted URL's
     */
    this.editor.clipboard.addMatcher(
      Node.TEXT_NODE,
      (node: any, delta: any) => {
        if (typeof node.data !== 'string') {
          return
        }
        // @ts-ignore
        const links = linkify.find(node.data)

        if (links && links.length > 0) {
          const ops: any = []
          let str = node.data
          links.forEach((match: any) => {
            const split = str.split(match.value)
            const beforeLink = split.shift()
            ops.push({ insert: beforeLink, attributes: {} })
            ops.push({ insert: match.value, attributes: { link: match.href } })
            str = split.join(match)
            str = split.join(' ')
          })
          ops.push({ insert: str })
          delta.ops = ops
        }

        if (!this.isFirefox) {
          const filtered = delta.ops.filter((item: any) => {
            return !IMAGE_MIME_REGEX.test(item.insert)
          })
          delta.ops = filtered
        }
        return delta
      },
    )

    /**
     * Autolink URLs when typing
     */
    this.editor.on(
      'text-change',
      async (delta: any, oldDelta: any, source: any) => {
        const hitBreak =
          delta.ops.length === 2 && this.isWhitespace(delta.ops[1].insert)
        if (hitBreak) {
          const content = this.editor.getContents().ops
          const newOps: any[] = this.updateDelta(content)
          if (content.length !== newOps.length) {
            /**
             * This updates the content of quill editor and
             * set selection to set the cursor at the end of the
             * text inserted
             */
            this.$nextTick(() => {
              this.editor.setContents(newOps)
              this.editor.setSelection(delta.length())
            })
          }
        }
        this.update()
      },
    )

    /**
     * To allow scroll on quill-mention
     */
    window.addEventListener('keydown', ev => {
      const mention: HTMLElement = document.querySelector('.ql-mention-list')
      const mentionItem: HTMLElement = document.querySelector('.selected')
      if (mention) {
        if (ev.keyCode === 38 || ev.keyCode === 40) {
          if (mentionItem) {
            mention.scrollTop = mentionItem.offsetTop - 6
          }
        }
      }
    })

    /**
     * Focus and set selection on IE when clicking on editor
     * without this the click only works on the first line
     */
    if (this.isInternetExplorer) {
      document.querySelector('.editor__canvas').addEventListener('click', e => {
        this.$nextTick(() => {
          const selection = this.editor.getSelection()
          const text = this.editor.getText().length
          selection
            ? this.editor.setSelection(selection)
            : this.editor.setSelection(text)
        })
      })
    }

    if (this.allowsAttachment && !this.isInternetExplorer) {
      this.addEventListenerDrag()
    }
  },

  beforeDestroy() {
    if (this.clipboard) {
      this.clipboard.destroy()
    }
    document.removeEventListener('drop', this.cancelDragOver)
    document.removeEventListener('dragenter', this.activateDragEnter)
    document.removeEventListener('dragover', this.activateDragEnter)
    document.removeEventListener('mouseout', this.cancelDragEnter)
    document.removeEventListener('dragend', this.cancelDragEnter)
    document
      .querySelector('body')
      .removeEventListener('mouseout', this.handleMouseOut)

    this.editor.container.removeEventListener('dragover', this.activateDragOver)
    this.editor.container.removeEventListener('dragleave', this.cancelDragOver)
  },

  methods: {
    /*
    * Add event listener drag
    */
    addEventListenerDrag() {
      /**
       *  Add event to catch drag over window and activate "drop here" message
       */
      document.addEventListener('dragenter', this.activateDragEnter)
      document.addEventListener('dragover', this.activateDragEnter)
      this.editor.container.addEventListener('dragover', this.activateDragOver)

      /** Add event to catch drag end to remove "drop here" message and catch drag leave editor and activate animation */
      document.addEventListener('dragend', this.cancelDragEnter)
      this.editor.container.addEventListener('dragleave', this.cancelDragOver)

      /** Prevent drop fom being caught when made outside editor */
      document.addEventListener(
        'drop',
        (event: DragEvent) => this.cancelDragOver,
      )

      /** Add event to catch mouse out of body to remove "drop here" message */
      document
        .querySelector('body')
        .addEventListener('mouseout', this.cancelDragEnter)

      /** Add event to catch mouse leaving window to remove "drop here" message */
      document.addEventListener('mouseout', this.handleMouseOut)
    },

    /**
     * When mouse goes outside the window remove "drop here" message
     */
    handleMouseOut(event: MouseEvent) {
      if (
        // @ts-ignore
        event.toElement == null &&
        this.dragEnter === true &&
        event.relatedTarget == null
      ) {
        this.dragEnter = false
      }
    },

    /**
     * Activates drag over editor
     */
    activateDragOver(event: DragEvent) {
      this.dragOver = true
    },

    /**
     * Cancels drag over editor
     */
    cancelDragOver(event: DragEvent) {
      event.preventDefault()
      event.stopPropagation()
      this.dragOver = false
    },

    /**
     * Activates drag on window and to diplay message
     */
    activateDragEnter(event: DragEvent) {
      event.preventDefault()
      event.stopPropagation()
      this.dragEnter = true
    },

    /**
     * Deactivate drag on window and to display message
     */
    cancelDragEnter(event: DragEvent | MouseEvent) {
      event.preventDefault()
      event.stopPropagation()
      this.dragEnter = false
    },

    /**
     * Update delta to add links on typing
     */
    updateDelta(content: any) {
      const newOps: any[] = []
      content.forEach((el: any) => {
        if (typeof el.insert === 'string') {
          // @ts-ignore
          const url = linkify.find(el.insert)
          if (url.length > 0) {
            const before = el.insert.split(url[0].value)[0]
            const after = el.insert.split(url[0].value)[1]

            if (before) {
              newOps.push({ ...el, insert: before })
            }

            newOps.push({
              // @ts-ignore
              insert: url[0].value,
              attributes: { link: url[0].href },
            })

            if (after) {
              newOps.push({ ...el, insert: after })
            }
          } else {
            newOps.push({ ...el })
          }
        } else {
          newOps.push({
            ...el,
            insert: el.insert,
          })
        }
      })

      return newOps
    },

    isWhitespace(text: string) {
      return text === ' ' || text === '\t' || text === '\n'
    },

    isVariableAUrl(nad: INameAndDescription) {
      return nad.name.toLowerCase().startsWith('http')
    },

    getCopyableVariableClassIfNecessary(nad: INameAndDescription) {
      return this.isVariableAUrl(nad) ? this.copyableVariableClass : ''
    },

    update() {
      this.output = this.editor.root.innerHTML
      this.$emit('input', this.output || '')
      this.emitSelectedAtMentionIDs()
    },

    async handleVariable(nad: INameAndDescription) {
      // e.g. {ReplierName} or https://www....
      const value = nad.name
      // e.g. 'Replier Full Name' or 'Copy link to Ticket Portal'
      const label = nad.description
      const isUrl = this.isVariableAUrl(nad)
      const cursorPosition = this.getCurrentCursorPosition()
      let newCursorPosition

      // Note: URLs are attached to clipboardJS for copying (currently just for ticket portal URL)
      if (!isUrl) {
        this.editor.insertText(cursorPosition, value)
        newCursorPosition = cursorPosition.index + value.length
      }
      this.editor.setSelection(newCursorPosition)

      this.showVariableDropdown = true
      this.$nextTick(() => {
        this.showVariableDropdown = false
      })
    },

    /*
     * If none of the values exist (getSelection and savedRange)
     * return the length of the text
     */
    getCurrentCursorPosition(): any {
      if (this.editor.getSelection() && this.editor.getSelection().index) {
        return this.editor.getSelection().index
      }
      if (this.editor.selection && this.editor.selection.savedRange) {
        return this.editor.selection.savedRange
      }
      return this.editor.getLength() - 1
    },

    searchMentions(searchTerm: String, renderList: any, mentionChar: string) {
      if (searchTerm.length === 0) {
        renderList(this.quillMentionUsers, searchTerm)
      } else {
        this.matches = []
        for (const i in this.quillMentionUsers) {
          if (
            ~this.quillMentionUsers[i].value
              .toLowerCase()
              .indexOf(searchTerm.toLowerCase())
          ) {
            const matchValue = this.quillMentionUsers[i]
            this.matches.push(matchValue)
          }
        }
        renderList(this.matches, searchTerm)
      }
    },

    emitSelectedAtMentionIDs() {
      const delta = this.editor.getContents()
      const atMentionIDs = new Set()
      if (delta.ops.length) {
        delta.ops.forEach((element: any) => {
          if (element.insert.hasOwnProperty('mention')) {
            atMentionIDs.add(parseInt(element.insert.mention.id, 0))
          }
        })
      }

      if (this.quillMentionUsers.length > 0) {
        this.$emit('mentions-list', Array.from(atMentionIDs))
      }

      const mention: HTMLElement = document.querySelector(
        '.ql-mention-list-container',
      )
      const bodyWidth: Element = document.querySelector('.el-card__body')
      const card: Element =
        document.querySelector('.child-detail-card__actions') ||
        document.querySelector('.entity-child-form') ||
        document.querySelector('.dd-modal__body')

      if (card && this.quillMentionUsers.length > 0) {
        const mentionLeft = parseFloat(window.getComputedStyle(mention).left)
        const width = parseFloat(window.getComputedStyle(mention).width)
        const cardWidth =
          parseFloat(window.getComputedStyle(bodyWidth).width) -
          parseFloat(
            window.getComputedStyle(card).getPropertyValue('padding-left'),
          )

        const total = mentionLeft + width

        if (total > cardWidth) {
          const remainingRight = window.innerWidth
          mention.style.transform =
            'translateX(-' + (mentionLeft + width - cardWidth + 15) + 'px)'
        } else {
          mention.style.transform = 'translateX(0)'
        }
      }
    },

    /**
     * Triggers click on button for el-upload
     */
    triggerUpload(e: any) {
      e.preventDefault()
      // @ts-ignore
      this.$refs.btnUpload.click()
    },

    handlerPicker(e: any) {
      e.preventDefault()
    },
    async handleRemove(file: any, fileList: any) {
      try {
        this.fileList = this.fileList.filter((item: any) => item.id !== file.id)
        this.filelistRemoved.push(file.id)
        this.hasAttachment = this.fileList.length > 0
        this.$emit('file-list-removed', file.id)
      } catch (error) {
        this.$messaging.showGenericError()
      }
    },

    handleError(err: any, file: any, fileList: any) {
      this.fileList = fileList
    },

    handleChange(file: any, fileList: any) {
      try {
        const reader = new FileReader()
        this.hasAttachment = true
        reader.onload = async () => {
          try {
            this.isMobileActionSheetOpen = false
            this.uploadingFile = true
            let response

            if (this.ticketPortalConvoId) {
              response = await TicketPortalAPI.post_FilesUpload(
                this.ticketPortalConvoId,
                {
                  files: [{ file: reader.result.toString() }],
                  form: [{ name: file.name }],
                },
              )
            } else {
              response = await FilesAPI.post_Upload({
                files: [{ file: reader.result.toString() }],
                form: [{ name: file.name }],
              })
            }
            if (response.id) {
              file.id = response.id
              file.uid = file.uid ? file.uid : Date.now() + file.id
              this.fileList.push(file)

              const upload = this.fileList.filter(
                item => item.status === 'ready',
              )

              this.$emit('files-list', upload)
            } else {
              this.hasAttachment = this.fileList.length > 0
              throw new Error()
            }
          } catch (error) {
            this.$error.emit(error, true)
          } finally {
            this.uploadingFile = false
          }
        }

        reader.readAsDataURL(file.raw)
      } catch (error) {
        this.$error.emit(error, true)
      }
    },

    overwriteBody(value: string) {
      if (!this.isInternetExplorer) {
        this.editor.root.innerHTML = value
      } else {
        const editor = document.querySelector('.ql-editor')
        editor.innerHTML = value
      }
    },

    handleFocus() {
      if (!this.isEditorFocused) {
        this.isEditorFocused = true
      }
    },

    handleBlur() {
      if (this.isEditorFocused) {
        this.isEditorFocused = false
      }
    },

    /**
     * Handle possibility of multiple files to be uploaded
     */
    handleGoogleDriveCallback(docs: any) {
      docs.forEach((element: any) => {
        this.handleGoogleDriveUpload(element)
      })
    },

    /**
     * Upload each file to DD api
     */
    async handleGoogleDriveUpload(file: any) {
      try {
        // Prevent file names bigger than 256 characters (API doesn't support bigger strings)
        if (file.name && file.name.length > 255) {
          this.$messaging.showError(
            'File name must be smaller than 255 characters.',
          )
          return
        }

        this.uploadingFile = true
        const response = await FilesAPI.post_LinkFromGoogleDrive({
          files: [{ fileUrl: file.url }],
          form: [{ name: file.name }],
        })
        if (response.id) {
          const newFile = {
            id: response.id,
            name: file.name,
            percentage: 0,
            size: file.size,
            status: 'ready',
            uid: file.uid,
          }

          this.fileList.push(newFile)
          const upload = this.fileList.filter(item => item.status === 'ready')

          this.$emit('files-list', upload)
          this.hasAttachment = true
        } else {
          this.hasAttachment = this.fileList.length > 0
          throw new Error()
        }
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.uploadingFile = false
      }
    },
  },
})
